import React, { useEffect, useState } from 'react';
import { Dialog, Button, Label, Select } from '@repo/ui';
import { Icon } from '@repo/ui';

interface BulkSimulateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (eventType: 'update' | 'delete') => Promise<void>;
  selectedCount: number;
  isLoading?: boolean;
}

interface BulkSimulateRequest {
  eventType: 'update' | 'delete';
}

export const BulkSimulateModal: React.FC<BulkSimulateModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  selectedCount,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState<BulkSimulateRequest>({
    eventType: 'update',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await onSubmit(formData.eventType);
      onClose();
    } catch (error: any) {
      // Error handling is done in the parent component
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} size="md">
      <div className="flex items-center space-x-3 p-6 border-b border-xtnd-dark-100 dark:border-xtnd-dark-600">
        <div className="flex-shrink-0">
          <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
            <Icon name="HiLightningBolt" size="md" className="text-blue-600 dark:text-blue-400" />
          </div>
        </div>
        <div>
          <h2 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-white">
            Bulk Retry Last Events
          </h2>
          <p className="text-sm text-xtnd-dark-500 dark:text-xtnd-dark-300 mt-1">
            Retry last events for {selectedCount} selected trust request{selectedCount > 1 ? 's' : ''}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="p-6">
        <div className="space-y-6">
          {/* Event Type */}
          <div>
            <Label htmlFor="eventType">
              Event Type <span className="text-red-500">*</span>
            </Label>
            <Select
              options={[
                { value: 'update', label: 'Update' },
                { value: 'delete', label: 'Delete' },
              ]}
              value={formData.eventType}
              onChange={value =>
                setFormData({
                  eventType: value as 'update' | 'delete',
                })
              }
              disabled={isLoading}
            />
            <p className="text-xs text-xtnd-dark-500 dark:text-xtnd-dark-300 mt-1">
              Select the event type to retry for all selected trust requests.
            </p>
          </div>

          {/* Summary */}
          <div className="bg-xtnd-light rounded-lg p-4">
            <h3 className="text-sm font-medium text-xtnd-dark mb-2">Summary</h3>
            <div className="text-sm text-xtnd-dark-600 space-y-1">
              <p><strong>Selected Trust Requests:</strong> {selectedCount}</p>
              <p><strong>Event Type:</strong> {formData.eventType === 'update' ? 'Update' : 'Delete'}</p>
            </div>
            <p className="text-xs text-xtnd-dark-500 mt-3">
              This will retry the last {formData.eventType} event{selectedCount > 1 ? 's' : ''} for {selectedCount} trust request{selectedCount > 1 ? 's' : ''}. Only eligible trust requests will be processed.
            </p>
          </div>
        </div>

        <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-xtnd-dark-100 dark:border-xtnd-dark-600">
          <Button type="button" variant="outline" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={isLoading}
            disabled={selectedCount === 0}
          >
            {isLoading ? 'Retrying...' : `Retry ${selectedCount} Last Event${selectedCount > 1 ? 's' : ''}`}
          </Button>
        </div>
      </form>
    </Dialog>
  );
};